import Button from '@gaia/components/Button';
import useRequestFreeTrial from '@gaia/hooks/useRequestFreeTrial';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import AuthModalContext from '@map/contexts/AuthModalContext';
import {Close as CloseIcon} from '@mui/icons-material';
import {
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import {useContext, useEffect, useState} from 'react';

import freeTrial from './free-trial.svg';

const closeButtonStyle = {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
};

const dialogContainerStyle = {
    padding: {
        xs: theme.spacing(2),
        md: theme.spacing(4),
    },
};

const graphicStyle = {
    display: 'flex',
    margin: 'auto',
    marginBottom: theme.spacing(2),
};

interface FreeTrialModalProps {
    shouldShowIfEligible: boolean;
}

export function FreeTrialModal({shouldShowIfEligible}: FreeTrialModalProps) {
    const [isOpen, setIsOpen] = useState(false);
    const {isAuthenticated, isEligibleForTrial} = useUser();
    const setAuthModalState = useContext(AuthModalContext);

    // Prompt the user to log in if they are unauthenticated
    useEffect(() => {
        if (shouldShowIfEligible && !isAuthenticated) {
            setAuthModalState({type: 'login', analyticsContext: 'free premium trial'});
        }
    }, [setAuthModalState, shouldShowIfEligible, isAuthenticated]);

    // Show the modal if the user is eligible
    useEffect(() => {
        if (shouldShowIfEligible && isEligibleForTrial) {
            setIsOpen(true);
            ampli.viewTrialMessage({trialIdentifier: 'trial97'});
        }
    }, [shouldShowIfEligible, isEligibleForTrial]);

    const {
        requestFreeTrial,
        isTrialRequestProcessing,
        wasFreeTrialActivated,
        requestFailureMessage,
    } = useRequestFreeTrial();

    useEffect(() => {
        if (wasFreeTrialActivated) {
            setIsOpen(false);
        }
    }, [wasFreeTrialActivated]);

    if (!shouldShowIfEligible) return null;

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
            <Container sx={dialogContainerStyle}>
                <DialogTitle>
                    <IconButton
                        aria-label='Close this offer'
                        onClick={() => setIsOpen(false)}
                        size='large'
                        sx={closeButtonStyle}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent style={{maxWidth: 320, boxSizing: 'border-box'}}>
                    <img height={209} width={209} alt='' src={freeTrial} style={graphicStyle} />
                    <Typography
                        component='h2'
                        variant='h4'
                        style={{marginBottom: theme.spacing(2)}}
                    >
                        Unlock Premium for 7 Days
                    </Typography>
                    <Typography variant='body1' style={{marginBottom: theme.spacing(2)}}>
                        Get the full map catalog, and customize maps and waypoints to tailor Gaia
                        GPS to your exact adventure. Take maps offline so you can always find your
                        way — even without cell service.
                    </Typography>
                </DialogContent>
                <DialogActions
                    style={{
                        marginBottom: theme.spacing(2),
                        flexDirection: 'column',
                    }}
                >
                    <Button
                        appearance='primary'
                        size='large'
                        fullWidth
                        style={{fontSize: 14, display: 'block'}}
                        onClick={() => requestFreeTrial()}
                        disabled={isTrialRequestProcessing}
                    >
                        Start Your 7 Day Trial
                    </Button>
                    {wasFreeTrialActivated === false && (
                        <span
                            style={{
                                color: theme.palette.error.main,
                                textAlign: 'center',
                                margin: '0 auto',
                                display: 'block',
                                maxWidth: 304,
                                paddingTop: theme.spacing(1),
                            }}
                        >
                            {requestFailureMessage}
                        </span>
                    )}
                </DialogActions>
            </Container>
        </Dialog>
    );
}
